import styled from '@xstyled/styled-components';
import React, { FC, useEffect, useMemo } from 'react';
import { RightMatchItem, useQuizMatchStore } from '../QuizMatchStore';
import { CorrectBadge } from './CorrectBadge';
import { IncorrectBadge } from './IncorrectBadge';
import { useColor } from './useColor';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import DropFolder from './dropFolder.png';
import { useEditorStore } from '~/shell/Store';

const BoxBorder = styled.button<{ highlightColor: string }>`
  border-radius: 22px;
  width: 220px;
  height: 175px;
  position: relative;
  margin-left: auto;
  margin-right: auto;
  background-color: transparent;
  cursor: pointer;
  .badge {
    margin-top: 30px;
    margin-right: 5px;
  }
  &:focus {
    .BoxShape {
      outline: 2px solid var(--color-focus-ring);
      outline-offset: 2px;
    }
  }
`;

const BoxShape = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  color: #ffffff;
  background-image: url(${DropFolder});
  background-color: #9a9a9a;
  background-position: center;
  background-size: cover;
  padding: md;
  border-radius: xl;
  height: 175px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  line-height: 18px;
  text-align: center;
`;

const Item = styled.div`
  position: absolute;
  opacity: 1;
  background-color: #fff;
  width: 90%;
  height: 80%;
  padding: 16px;
  border-radius: 20px;
  color: #000;
  text-align: center;
  margin-top: 14px;
  font-size: 13px;
  overflow: auto;
  ul {
    li {
      text-align: center;
      img {
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 10px;
      }
    }
  }
`;

export const BoxButton: FC<
  RightMatchItem & {
    onClick?: () => void;
    onClickLeftButton?: (pairId: string) => void;
    item: RightMatchItem;
    disabled?: boolean;
    onDragOver?: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent) => void;
    onResult?: (correct: boolean) => void;
  }
> = ({
  label,
  pairId,
  leftItem,
  onClick,
  onClickLeftButton,
  disabled,
  onDragOver,
  onDrop,
  onResult
}) => {
  const unSelect = useQuizMatchStore(s => s.unSelect);
  const { editorMode, contentEditable, isReadOnlyView } = useEditorStore();
  const correct = useMemo(() => {
    if (leftItem) return pairId === leftItem.pairId;
  }, [leftItem]);
  const color = useColor(correct);
  const [playAudioEffectRightAnswer] = useAudioEffect(audioEffectRightAnswer);
  const [playAudioEffectWrongAnswer] = useAudioEffect(audioEffectWrongAnswer);

  useEffect(() => {
    if (correct !== undefined) {
      // Don't play audio effects in read-only mode
      if (!isReadOnlyView) {
        correct ? playAudioEffectRightAnswer() : playAudioEffectWrongAnswer();
        onResult?.(correct);
      }
      if (!correct) {
        setTimeout(() => {
          unSelect(pairId, leftItem, true);
          if (leftItem && leftItem.pairId) {
            onClickLeftButton!(leftItem.pairId);
          }
        }, 500);
      }
    }
  }, [correct, editorMode, contentEditable]);

  return (
    <BoxBorder
      disabled={disabled}
      tabIndex={0}
      highlightColor={color}
      className="BoxBorder"
      onClick={onClick}
      onKeyPress={e => {
        if (e.key === 'Enter') {
          onClick!();
        }
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {correct === true ? (
        <CorrectBadge
          className="badge"
          position="absolute"
          top="0"
          right="0"
          aria-hidden="true"
        />
      ) : null}
      {correct === false ? (
        <IncorrectBadge
          className="badge"
          position="absolute"
          top="0"
          right="0"
          aria-hidden="true"
        />
      ) : null}{' '}
      <BoxShape className="BoxShape">
        {leftItem && (
          <Item
            onClick={() => {
              if (!correct) {
                onClickLeftButton!(leftItem.pairId);
              }
            }}
          >
            {leftItem.image && leftItem.image.mediaUrl ? (
              <ul>
                <li>
                  <img
                    src={leftItem?.image?.mediaUrl}
                    height={55}
                    alt={leftItem.label}
                  />
                </li>
                <li>{leftItem.label}</li>
              </ul>
            ) : (
              leftItem.label
            )}
          </Item>
        )}
        {label}
      </BoxShape>
    </BoxBorder>
  );
};
